import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { putPatientOnHold } from '../../../services/admin/patient.service';
import { PatientLifecycleService, toIsoDate } from '../../../services/patientmr/patientLifecycle.service';
import { HOLD_REASON_OPTIONS } from '../../../constants/patientLifecycle';
import type { HoldReasonCode, PatientData } from '../../../dtos/admin/patient.dto';
import type { CancelCode } from '../../../dtos/scheduling/schedule.dto';
import { ScheduleService } from '../../../services/scheduling';
import { useToast } from '../../common/Toast';

/** Shift cancel reason for a hold reason (Phase 9A opt-in cancel). */
const HOLD_TO_CANCEL: Partial<Record<HoldReasonCode, CancelCode>> = {
  HOSPITALIZED: 'HOSPITALIZED',
  FAMILY_REQUEST: 'FAMILY',
  STAFFING_UNAVAILABLE: 'STAFF',
};

export interface HoldDialogProps {
  open: boolean;
  patientId: string;
  onCancel: () => void;
  /** Called with the refreshed patient after the hold is saved */
  onHeld: (patient: PatientData) => void;
}

/**
 * Put a patient on hold (HLD-03 / HLD-02). Replaces the old window.prompt.
 * Shows the number of future scheduled shifts and that they are NOT cancelled automatically.
 */
const HoldDialog: React.FC<HoldDialogProps> = ({ open, patientId, onCancel, onHeld }) => {
  const baseId = useId();
  const [reason, setReason] = useState<HoldReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shiftCount, setShiftCount] = useState<number | null>(null);
  const [shiftCountFailed, setShiftCountFailed] = useState(false);
  const [cancelShifts, setCancelShifts] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return undefined;
    setReason('');
    setNote('');
    setExpectedReturn('');
    setSubmitted(false);
    setError(null);
    setShiftCount(null);
    setShiftCountFailed(false);
    setCancelShifts(false);
    let cancelled = false;
    PatientLifecycleService.countFutureShifts(patientId)
      .then((count) => {
        if (!cancelled) setShiftCount(count);
      })
      .catch(() => {
        if (!cancelled) setShiftCountFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, patientId]);

  const today = toIsoDate(new Date());
  const reasonMissing = !reason;
  const noteMissing = reason === 'OTHER' && !note.trim();
  const returnBeforeToday = !!expectedReturn && expectedReturn < today;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (reasonMissing || noteMissing || returnBeforeToday || busy || !reason) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await putPatientOnHold(patientId, {
        holdReasonCode: reason,
        holdNote: note.trim() || undefined,
        holdStartDate: today,
        holdExpectedReturnDate: expectedReturn || undefined,
      });
      if (cancelShifts && shiftCount) {
        // Opt-in (HLD-02 never cancels on its own). The series keep running; held shifts show amber.
        try {
          const code = HOLD_TO_CANCEL[reason] || 'OTHER';
          const r = await ScheduleService.bulk('cancelPatientFuture', { patientId, code, note: code === 'OTHER' ? 'Patient on hold' : undefined, endSeries: false });
          showToast(`Cancelled ${r.cancelled ?? 0} future shift(s)`, { variant: 'success' });
        } catch {
          showToast('The hold was saved, but the shifts were not cancelled. Cancel them from the Schedule tab.', { variant: 'warning' });
        }
      }
      onHeld(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to put the patient on hold.');
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${baseId}-title`;
  const reasonId = `${baseId}-reason`;
  const noteId = `${baseId}-note`;
  const returnId = `${baseId}-return`;

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={titleId}>
      <form onSubmit={handleSubmit} className="text-left" noValidate>
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          Put patient on hold
        </h3>
        <p className="mt-1 text-sm text-slate-600">Visits can't be started while the patient is on hold.</p>

        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="hold-shift-count">
          {shiftCountFailed ? (
            <span>Couldn't load scheduled shifts. Check the schedule before continuing.</span>
          ) : shiftCount === null ? (
            <span>Checking scheduled shifts…</span>
          ) : (
            <span>
              <strong>{shiftCount}</strong> future scheduled shift{shiftCount === 1 ? '' : 's'}.
            </span>
          )}{' '}
          <span>They will NOT be cancelled automatically — review them in the Schedule tab.</span>
          {!!shiftCount && (
            <label className="mt-2 flex min-h-[40px] items-center gap-2 font-medium">
              <input type="checkbox" className="h-4 w-4" checked={cancelShifts} onChange={(e) => setCancelShifts(e.target.checked)} disabled={busy} />
              Also cancel these {shiftCount} shift{shiftCount === 1 ? '' : 's'} now
            </label>
          )}
        </div>

        <label htmlFor={reasonId} className="block mt-4 text-sm font-medium text-slate-700">
          Reason <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <select
          id={reasonId}
          value={reason}
          onChange={(e) => setReason(e.target.value as HoldReasonCode | '')}
          disabled={busy}
          aria-invalid={submitted && reasonMissing}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-sm"
        >
          <option value="">Select a reason…</option>
          {HOLD_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {submitted && reasonMissing && <p className="mt-1 text-xs text-red-700">Select a reason.</p>}

        <label htmlFor={noteId} className="block mt-3 text-sm font-medium text-slate-700">
          Note {reason === 'OTHER' && <span className="text-red-600" aria-hidden="true">*</span>}
        </label>
        <textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={1000}
          disabled={busy}
          aria-invalid={submitted && noteMissing}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
          placeholder={reason === 'OTHER' ? 'Required for Other' : 'Optional'}
        />
        {submitted && noteMissing && <p className="mt-1 text-xs text-red-700">A note is required when the reason is Other.</p>}

        <label htmlFor={returnId} className="block mt-3 text-sm font-medium text-slate-700">
          Expected return date
        </label>
        <input
          id={returnId}
          type="date"
          value={expectedReturn}
          min={today}
          onChange={(e) => setExpectedReturn(e.target.value)}
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
        />
        {submitted && returnBeforeToday && (
          <p className="mt-1 text-xs text-red-700">The expected return date can't be in the past.</p>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Put on hold'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default HoldDialog;
